import { HttpErrorResponse } from '@angular/common/http';
import { AfterViewInit, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTable, MatTableModule } from '@angular/material/table';
import { Printer } from 'src/app/domain/printer/printer';
import { PrintJob } from 'src/app/domain/printJob/print-job';
import { PrintJobService } from 'src/app/domain/printJob/print-job-service.service';
import { PrintJobTableDataSource } from './print-job-table-data-source';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CommonModule } from '@angular/common';
import { MatTabsModule } from '@angular/material/tabs';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface ViewDialogContent {
  /**
   * the selected Printer
   */
  printer: Printer;

  /**
   * contains other members of the printerclass
   */
  classMembers: string[];
}

@Component({
  selector: 'app-view-printer-dialog',
  templateUrl: './view-printer-dialog.component.html',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatTabsModule,
    MatCardModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    TranslateModule
  ],
  styleUrls: ['./view-printer-dialog.component.css']
})
export class ViewPrinterDialogComponent implements OnInit, AfterViewInit {

  printJobs: PrintJob[] = [];
  @ViewChild(MatTable) table: MatTable<PrintJob>;
  dataSource: PrintJobTableDataSource;
  /** Columns displayed in the table. Columns IDs can be added, removed, or reordered. */
  displayedColumns = ['jobID', 'jobName', 'userName', 'size', 'pagesPrinted', 'jobState', 'actions'];
  /** Tabs in the Dialog */
  tabActiveJobs: string;
  tabCompletedJobs: string;
  tabs: string[];

  activePrintJobStates = ['PENDING', 'PENDING_HELD', 'PROCESSING', 'PROCESSING_STOPPED'];
  finishedPrintJobStates = ['CANCELED', 'ABORTED', 'COMPLETED', 'UNDEFINED', 'UNDEFINED_NULL'];

  selectedTab = 0;

  constructor(
    public dialogRef: MatDialogRef<ViewPrinterDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ViewDialogContent,
    private printJobService: PrintJobService,
    private snackBar: MatSnackBar,
    private translate: TranslateService) {
    this.initPrintJobTabTranslation();
  }

  ngOnInit(): void {
    this.dataSource = new PrintJobTableDataSource(this.printJobService);
    this.dataSource.getPrintJobs(this.data.printer.name, 'NOT_COMPLETED');
  }

  ngAfterViewInit(): void {
    this.table.dataSource = this.dataSource;
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  /*******************************************************************
   *
   * PUBLIC API
   *
   ******************************************************************/

  loadDataForSelected(tabIndex: number) {
    switch (tabIndex) {
      case 0: {
        this.dataSource.getPrintJobs(this.data.printer.name, 'NOT_COMPLETED');
        this.selectedTab = 0;
        break;
      }
      case 1: {
        this.dataSource.getPrintJobs(this.data.printer.name, 'COMPLETED');
        this.selectedTab = 1;
        break;
      }
      default: {
        console.error('TabChange: ' + tabIndex);
        break;
      }
    }
  }

  moveJob(jobId: number, targetPrinter: string) {
    console.log('moving Job with ID ' + jobId + ' to ' + targetPrinter);
    this.printJobService.moveJob(this.data.printer.name, targetPrinter, String(jobId))
      .subscribe(
        () => {
          console.log('movedResponse');
          this.loadDataForSelected(this.selectedTab);
        },
        (err) => this.handleError(err)
      );
  }

  holdJob(jobId: number) {
    console.log('holdJob: ' + jobId);
    this.printJobService.holdJob(this.data.printer.name, String(jobId))
      .subscribe(
        () => {
          console.log('holdResponse');
          this.loadDataForSelected(this.selectedTab);
        },
        (err) => this.handleError(err)
      );
  }

  releaseJob(jobId: number) {
    console.log('releaseJob: ' + jobId);
    this.printJobService.releaseJob(this.data.printer.name, String(jobId))
      .subscribe(
        () => {
          console.log('releaseResponse');
          this.loadDataForSelected(this.selectedTab);
        },
        (err) => this.handleError(err)
      );
  }

  cancelJob(jobId: number) {
    console.log('cancelJob: ' + jobId);
    this.printJobService.cancelJob(this.data.printer.name, String(jobId))
      .subscribe(
        () => {
          console.log('cancelResponse');
          this.loadDataForSelected(this.selectedTab);
        },
        (err) => this.handleError(err)
      );
  }

  /*******************************************************************
   *
   * PRIVATE METHODS
   *
   ******************************************************************/

  /**
   * Gets the Text from the already loaded Translation Files and constructs the tab string
   */
  private initPrintJobTabTranslation(): void {
    this.translate.get('printerTableViewPrinterDialog.tabActiveJobs').subscribe(
      (text) => this.tabActiveJobs = text,
      (err) => console.error(err)
    );
    this.translate.get('printerTableViewPrinterDialog.tabCompletedJobs').subscribe(
      (text) => this.tabCompletedJobs = text,
      (err) => console.error(err)
    );
    this.tabs = [this.tabActiveJobs + ' ' + this.data.printer.name, ' ' + this.tabCompletedJobs + ' ' + this.data.printer.name];
  }

  private openSnackBar(message: string, action: string, duration: number) {
    this.snackBar.open(message, action, {
      duration,
    });
  }

  private handleError(error: any) {
    console.error(error);
    if (error instanceof HttpErrorResponse) {
      this.openSnackBar(error.message, 'OK', 10000);
    }
  }
}
